import { keepPreviousData, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { listJobs, getJob, createJob, retryJob, cancelJob, type Job } from "@/lib/api/jobs";
import type { CreateJobInput, JobStatus } from "@repo/shared";

const ACTIVE_JOB_STATUSES: JobStatus[] = ["pending", "processing"];

// While any job on screen is still running, poll so its status/progress advances
// without the user refreshing.
const ACTIVE_JOB_POLL_MS = 5000;

function isJobActive(status: JobStatus): boolean {
  return ACTIVE_JOB_STATUSES.includes(status);
}

// Query keys factory for type-safe cache management
export const jobKeys = {
  all: ["jobs"] as const,
  lists: () => [...jobKeys.all, "list"] as const,
  list: (filters?: {
    limit?: number;
    offset?: number;
    cursor?: string;
    type?: string;
    status?: string;
    q?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => [...jobKeys.lists(), filters] as const,
  details: () => [...jobKeys.all, "detail"] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
};

// Query options for fetching all jobs
export const jobsQueryOptions = (params?: {
  limit?: number;
  cursor?: string;
  type?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}) =>
  queryOptions({
    queryKey: jobKeys.list(params),
    queryFn: () => listJobs(params),
    refetchInterval: (query) =>
      query.state.data?.data.some((job) => isJobActive(job.status)) ? ACTIVE_JOB_POLL_MS : false,
  });

// One page of the job history, filtered and counted server-side: `total` covers
// every matching job, not just the fetched page, so the pagination footer can
// walk the whole history. `type`/`status` are comma-separated lists.
export const jobsPageQueryOptions = (params: {
  page: number;
  pageSize: number;
  type?: string;
  status?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
}) => {
  const request = {
    limit: params.pageSize,
    offset: (params.page - 1) * params.pageSize,
    type: params.type,
    status: params.status,
    q: params.q,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
  };

  return queryOptions({
    queryKey: jobKeys.list(request),
    queryFn: () => listJobs(request),
    // Keep the current page on screen while the next one loads, so paging and
    // filtering don't flash the empty/loading state.
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.data.some((job) => isJobActive(job.status)) ? ACTIVE_JOB_POLL_MS : false,
  });
};

// Query options for fetching a single job
export const jobQueryOptions = (id: string) =>
  queryOptions({
    queryKey: jobKeys.detail(id),
    queryFn: () => getJob(id),
    enabled: !!id,
    refetchInterval: (query) => {
      // Poll every 2 seconds while job is processing
      const job = query.state.data;
      if (job?.status === "pending" || job?.status === "processing") {
        return 2000;
      }
      return false;
    },
  });

// Mutation hook for creating a job
export function useCreateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateJobInput) => createJob(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

// Mutation hook for retrying a job
export function useRetryJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => retryJob(id),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(job.id) });
    },
  });
}

// Mutation hook for cancelling a job
export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cancelJob(id),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(job.id) });
    },
  });
}

export type { Job };
